/**
 * Beautyrest — Tier Cart Attribute Sync v4.1
 *
 * Ensures correct tier and tier_price are set on all cart line items,
 * and stamps the variant's non-Size option ("Series") as a line item
 * property so it carries into checkout.
 *
 * THREE-LAYER approach:
 * 1. Form submit interceptor — injects properties BEFORE add-to-cart
 * 2. Fetch interceptor       — injects properties on AJAX add-to-cart
 * 3. Cart sync               — fixes missing tier_price AND sets Series after items are in cart
 *                              (Series is written FIRST so it renders directly under the variant title)
 */

(function () {

  // ── Config ─────────────────────────────────────────────────────────────────
  const SERIES_LABEL = 'Series';   // checkout label for the non-size option (rename freely)
  // Recognized mattress SIZES, matched by VALUE (not by option name) so it works no
  // matter how each product names its options. Anything that isn't a size = the Series.
  const SIZE_VALUE_PATTERN = /^\s*(twin xl|twin|full xl|full|double|short queen|olympic queen|split queen|queen|split california king|california king|cal king|split king|king|single)\s*$/i;

  // ── Helpers ───────────────────────────────────────────────────────────────

  function getCookie(name) {
    const match = document.cookie.match(new RegExp('(?:^|; )' + name + '=([^;]*)'));
    return match ? decodeURIComponent(match[1]) : '';
  }

  function getTierFromCookie() {
    const t = getCookie('br_tier');
    if (t === 'tier2') return 'Tier2';
    if (t === 'tier1') return 'Tier1';
    return '';
  }

  function getTierPricesData() {
    const el = document.getElementById('br-tier-prices');
    if (!el) return null;
    try { return JSON.parse(el.textContent); } catch(e) { return null; }
  }

  function getTierPrice(variantId) {
    const tier = getCookie('br_tier');
    if (!tier) return '';
    const data = getTierPricesData();
    if (!data) return '';
    const prices = data.variants?.[String(variantId)];
    if (!prices) return '';
    const raw = tier === 'tier2' ? prices.tier2 : prices.tier1;
    if (!raw) return '';
    return String(raw).replace(/[^0-9.]/g, '');
  }

  // Build the "Series" value from a cart line's options: everything that ISN'T the Size option.
  // Reads /cart.js item.options_with_values, so it's complete regardless of how the item was added.
  // Returns '' for Size-only products (nothing extra to show).
  function getSeries(item) {
    const ov = item.options_with_values || [];
    return ov
      .filter(function (o) {
        return o && o.value && o.value !== 'Default Title' && !SIZE_VALUE_PATTERN.test(o.value);
      })
      .map(function (o) { return o.value; })
      .join(', ');
  }

  // Hide the redundant "Series" property row in the CART only.
  // The cart already shows the series as a native variant option (e.g. "Series Three Firm, Twin XL"),
  // so our Series property duplicates it there. The property stays on the line (checkout needs it,
  // because checkout omits the option) — we just hide its row on the cart page.
  function hideSeriesInCart() {
    if (!window.location.pathname.startsWith('/cart')) return;
    document.querySelectorAll('.cart-items__properties').forEach(function (el) {
      var dt   = el.querySelector('dt');
      var name = (dt ? dt.textContent : el.textContent) || '';
      name = name.trim().replace(/[:\uFF1A][\s\S]*$/, '').trim(); // text before the first colon
      if (name === SERIES_LABEL) el.style.display = 'none';
    });
  }

  // Re-hide whenever the cart re-renders (setting display doesn't trigger the observer)
  function startSeriesHideWatcher() {
    if (!window.location.pathname.startsWith('/cart')) return;
    hideSeriesInCart();
    try {
      var container = document.querySelector('cart-items-component, cart-items, .cart-items, .cart-items__table') || document.body;
      new MutationObserver(function () { hideSeriesInCart(); })
        .observe(container, { childList: true, subtree: true });
    } catch (e) {}
  }

  // ── Layer 1: Form submit interceptor ─────────────────────────────────────
  // Fires BEFORE form submits — most reliable for standard add-to-cart

  document.addEventListener('submit', function(e) {
    const form = e.target;
    if (!form || form.getAttribute('action') !== '/cart/add') return;

    const tier = getTierFromCookie();
    if (!tier) return;

    const variantInput = form.querySelector('[name="id"]');
    if (!variantInput) return;
    const variantId = variantInput.value;

    const tierPrice = getTierPrice(variantId);
    if (!tierPrice) return;

    // Set properties[tier]
    let tierInput = form.querySelector('[name="properties[tier]"]');
    if (tierInput) {
      tierInput.value = tier;
    } else {
      tierInput = document.createElement('input');
      tierInput.type = 'hidden';
      tierInput.name = 'properties[tier]';
      tierInput.value = tier;
      form.appendChild(tierInput);
    }

    // Set properties[tier_price]
    let tierPriceInput = form.querySelector('[name="properties[tier_price]"]');
    if (tierPriceInput) {
      tierPriceInput.value = tierPrice;
    } else {
      tierPriceInput = document.createElement('input');
      tierPriceInput.type = 'hidden';
      tierPriceInput.name = 'properties[tier_price]';
      tierPriceInput.value = tierPrice;
      form.appendChild(tierPriceInput);
    }

    console.log('[Beautyrest] Form submit: tier=' + tier + ' tier_price=' + tierPrice + ' variant=' + variantId);
  }, true); // capture phase

  // ── Layer 2: Fetch interceptor ────────────────────────────────────────────
  // Catches AJAX add-to-cart calls (Horizon uses fetch for add-to-cart)

  const _origFetch = window.fetch;
  window.fetch = function(url, options) {
    const urlStr = (typeof url === 'string') ? url : (url?.url || '');

    if (urlStr.includes('/cart/add')) {
      const tier = getTierFromCookie();
      if (tier) {
        const rawBody = options?.body;

        if (rawBody instanceof FormData) {
          // Handle FormData (Horizon's default)
          const variantId = rawBody.get('id');
          if (variantId) {
            const tierPrice = getTierPrice(variantId);
            if (tierPrice) {
              rawBody.set('properties[tier]', tier);
              rawBody.set('properties[tier_price]', tierPrice);
              console.log('[Beautyrest] FormData add: tier=' + tier + ' tier_price=' + tierPrice + ' variant=' + variantId);
            }
          }
        } else {
          // Handle JSON body
          let body = {};
          try { body = JSON.parse(rawBody || '{}'); } catch(e) {}
          const variantId = body.id || body.items?.[0]?.id;
          if (variantId) {
            const tierPrice = getTierPrice(variantId);
            if (tierPrice) {
              if (body.items) {
                body.items = body.items.map(item => ({
                  ...item,
                  properties: { ...(item.properties || {}), tier, tier_price: tierPrice }
                }));
              } else {
                body.properties = { ...(body.properties || {}), tier, tier_price: tierPrice };
              }
              options = { ...options, body: JSON.stringify(body) };
              console.log('[Beautyrest] JSON add: tier=' + tier + ' tier_price=' + tierPrice);
            }
          }
        }
      }
    }

    // Handle cart/change and cart/update for sync
    const isCartChange = urlStr.includes('/cart/change') && !((options?.body || '').toString().includes('tier_price'));
    const result = _origFetch.apply(this, arguments);
    if (isCartChange) {
      result.then(() => setTimeout(syncTierAttributes, 800));
    }

    return result;
  };

  // ── Layer 3: Cart sync ────────────────────────────────────────────────────
  // Runs after cart loads — fixes any missing tier_price and sets Series on cart page

  let syncPending = false;

  async function syncTierAttributes() {
    if (syncPending) return;
    syncPending = true;

    try {
      const res = await fetch('/cart.js', { cache: 'no-store' });
      const cart = await res.json();
      if (!cart.items?.length) { syncPending = false; return; }

      // Get tier from cookie first, fall back to cart attribute
      const cookieTier = getTierFromCookie();
      const tier = cookieTier || cart.attributes?.tier || '';
      if (!tier) { syncPending = false; return; }

      // Step 1 — Set cart-level tier attribute
      const currentTier = cart.attributes?.tier;
      if (currentTier !== tier) {
        await fetch('/cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ attributes: { tier } })
        });
      }

      // Step 2 — Fix tier_price + set Series on each line item using DOM prices
      if (!window.location.pathname.startsWith('/cart')) {
        syncPending = false;
        return;
      }

      const rows = document.querySelectorAll('.cart-items tr');
      const domPrices = {};

      rows.forEach(row => {
        const comp = row.querySelector('cart-quantity-selector-component[data-variant-id]');
        const priceEl = row.querySelector('.cart-items__price text-component');
        if (!comp || !priceEl) return;

        const variantId = comp.getAttribute('data-variant-id');
        const priceText = (priceEl.getAttribute('value') || priceEl.textContent || '').trim();
        const lineTotal = parseFloat(priceText.replace(/[^0-9.]/g, ''));
        if (!variantId || isNaN(lineTotal) || lineTotal <= 0) return;

        const item = cart.items.find(i => String(i.variant_id) === String(variantId));
        if (!item) return;

        const unitPrice = (lineTotal / item.quantity).toFixed(2);
        domPrices[item.key] = { unitPrice, item };
      });

      for (const [key, { unitPrice, item }] of Object.entries(domPrices)) {
        const series   = getSeries(item);
        const priceOk  = item.properties?.tier_price === unitPrice;
        const seriesOk = !series || item.properties?.[SERIES_LABEL] === series;
        if (priceOk && seriesOk) continue;

        // Build with Series FIRST so it renders directly under the variant title at checkout
        const props = {};
        if (series) props[SERIES_LABEL] = series;
        Object.keys(item.properties || {}).forEach(function (k) {
          if (k !== SERIES_LABEL) props[k] = item.properties[k];
        });
        props.tier = tier;
        props.tier_price = unitPrice;

        await fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            id: key,
            quantity: item.quantity,
            properties: props
          })
        });
        console.log('[Beautyrest] Synced line:', item.title, '→', JSON.stringify(props));
      }

    } catch(err) {
      console.warn('[Beautyrest Tier Sync]', err);
    } finally {
      syncPending = false;
    }
  }

  // Run sync + series-hide on cart page load
  function _onCartReady() {
    if (!window.location.pathname.startsWith('/cart')) return;
    setTimeout(syncTierAttributes, 800);
    startSeriesHideWatcher();
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', _onCartReady);
  } else {
    _onCartReady();
  }

  // Re-sync on cart events
  document.addEventListener('cart:updated', function() { setTimeout(syncTierAttributes, 500); hideSeriesInCart(); });
  document.addEventListener('cart:refresh', function() { setTimeout(syncTierAttributes, 500); hideSeriesInCart(); });

}());